import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { GlobalsService } from '../../services/globals.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { SystemService } from '../../services/system.service';
import { EmailIntakeService } from '../../orders/email-intake/email-intake.service';

@Component({
	selector: 'app-mail-intake-settings',
	templateUrl: './mail-intake-settings.component.html',
	styleUrls: ['./mail-intake-settings.component.scss'],
})
export class MailIntakeSettingsComponent implements OnInit, OnDestroy {
	intakeForm: UntypedFormGroup;
	color = 'blue';
	aiStatus: { api_key_configured?: boolean; model?: string } | null = null;
	connectionTesting = false;
	private destroy$ = new Subject<void>();

	constructor(
		private fb: UntypedFormBuilder,
		private globalsService: GlobalsService,
		private globalSearchService: GlobalSearchService,
		private systemService: SystemService,
		private route: ActivatedRoute,
		private router: Router,
		private emailIntakeService: EmailIntakeService,
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.intakeForm = this.fb.group({
			config_mail_intake_provider: ['microsoft_graph'],
			config_mail_graph_enabled: [false],
			config_mail_graph_mailbox: [''],
			config_mail_graph_folder: [''],
			config_mail_graph_oauth_tenant_id: [''],
			config_mail_graph_oauth_client_id: [''],
			config_mail_graph_oauth_refresh_token: [''],
			config_mail_imap_hostname: [''],
			config_mail_imap_port: [993],
			config_mail_imap_username: [''],
			config_mail_imap_password: [''],
			config_mail_imap_folder: ['INBOX'],
			config_email_intake_retention_days: [120],
			email_intake_ai: this.fb.group({
				enabled: [false],
				classifierEnabled: [false],
				extractorEnabled: [false],
				model: [''],
				instructions: [''],
			}),
		});

		this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
			if (params['oauth_ok']) {
				this.globalSearchService.showNotification('Microsoft Graph authorization saved.', 'success', 'bottom', 'right');
				this.clearOAuthQueryParams();
				this.loadForm();
			} else if (params['oauth_error']) {
				this.globalSearchService.showNotification('Microsoft Graph authorization failed or was cancelled.', 'warning', 'bottom', 'right');
				this.clearOAuthQueryParams();
			}
		});

		this.loadForm();
		this.loadAiStatus();
	}

	loadAiStatus(): void {
		this.emailIntakeService.aiStatus().pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
			if (res?.success) {
				this.aiStatus = { api_key_configured: !!res.api_key_configured, model: res.model || '' };
			}
		});
	}

	testMailboxConnection(): void {
		this.connectionTesting = true;
		this.emailIntakeService.testConnection().pipe(takeUntil(this.destroy$)).subscribe(
			(res: any) => {
				this.connectionTesting = false;
				const ok = !!res?.success;
				this.globalSearchService.showNotification(
					res?.message || (ok ? 'Connection OK' : 'Connection failed'),
					ok ? 'success' : 'warning',
					'bottom',
					'right',
				);
			},
			() => {
				this.connectionTesting = false;
				this.globalSearchService.showNotification('Connection test failed', 'warning', 'bottom', 'right');
			},
		);
	}

	openIntakeHub(): void {
		this.router.navigateByUrl('/orders/email-intake');
	}

	get aiForm(): UntypedFormGroup {
		return this.intakeForm.get('email_intake_ai') as UntypedFormGroup;
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private clearOAuthQueryParams(): void {
		const q = { ...this.route.snapshot.queryParams };
		delete q['oauth_ok'];
		delete q['oauth_error'];
		this.router.navigate([], { relativeTo: this.route, queryParams: q, replaceUrl: true });
	}

	loadForm(): void {
		this.globalsService.getEmailIntakeSettings().pipe(takeUntil(this.destroy$)).subscribe((result: any) => {
			if (!result) {
				return;
			}
			const ai = result.email_intake_ai || {};
			this.intakeForm.patchValue({
				config_mail_intake_provider: result.config_mail_intake_provider || 'microsoft_graph',
				config_mail_graph_enabled: result.config_mail_graph_enabled === 'true' || result.config_mail_graph_enabled === true,
				config_mail_graph_mailbox: result.config_mail_graph_mailbox || '',
				config_mail_graph_folder: result.config_mail_graph_folder || '',
				config_mail_graph_oauth_tenant_id: result.config_mail_graph_oauth_tenant_id || '',
				config_mail_graph_oauth_client_id: result.config_mail_graph_oauth_client_id || '',
				config_mail_graph_oauth_refresh_token: result.config_mail_graph_oauth_refresh_token || '',
				config_mail_imap_hostname: result.config_mail_imap_hostname || '',
				config_mail_imap_port: result.config_mail_imap_port ? parseInt(result.config_mail_imap_port, 10) : 993,
				config_mail_imap_username: result.config_mail_imap_username || '',
				config_mail_imap_password: result.config_mail_imap_password || '',
				config_mail_imap_folder: result.config_mail_imap_folder || 'INBOX',
				config_email_intake_retention_days: result.config_email_intake_retention_days
					? parseInt(result.config_email_intake_retention_days, 10)
					: 120,
				email_intake_ai: {
					enabled: !!ai.enabled,
					classifierEnabled: !!ai.classifierEnabled,
					extractorEnabled: !!ai.extractorEnabled,
					model: ai.model || '',
					instructions: ai.instructions || '',
				},
			});
		});
	}

	startMicrosoftGraphOAuth(): void {
		const path = this.router.url.split('?')[0];
		const returnUrl = `${window.location.origin}${window.location.pathname}#${path}?tab=mail-intake`;
		this.systemService.beginMailGraphOAuth(returnUrl).subscribe((res: any) => {
			if (res && res.success && res.authorize_url) {
				window.location.href = res.authorize_url;
			} else {
				this.globalSearchService.showNotification(
					res && res.message ? res.message : 'Could not start Microsoft Graph sign-in.',
					'warning',
					'bottom',
					'right',
				);
			}
		});
	}

	saveIntakeSettings(): void {
		if (!this.intakeForm.valid) {
			return;
		}
		const raw = { ...this.intakeForm.value };
		raw.email_intake_ai = { ...raw.email_intake_ai };
		this.globalsService.saveEmailIntakeSettings(raw).pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.loadForm();
			this.globalSearchService.showNotification('Business mail intake settings saved.', 'success', 'bottom', 'right');
		});
	}
}
